import { useState } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import userService from '../../services/userService';
import Modal from '../../components/Common/Modal';
import Button from '../../components/Common/Button';
import Input from '../../components/Common/Input';

const ROLES = [
  {
    value: 1,
    en: 'Employee',
    ar: 'موظف',
    descEn: 'View-only access. Can browse orders and products but cannot make changes.',
    descAr: 'صلاحية عرض فقط. يمكنه تصفح الطلبات والمنتجات دون إجراء تغييرات.',
    color: '#6b7280',
  },
  {
    value: 2,
    en: 'Order Manager',
    ar: 'مدير الطلبات',
    descEn: 'Can view and update order statuses, manage customer communications.',
    descAr: 'يمكنه عرض الطلبات وتحديث حالاتها وإدارة التواصل مع العملاء.',
    color: '#3b82f6',
  },
  {
    value: 3,
    en: 'Product Manager',
    ar: 'مدير المنتجات',
    descEn: 'Can manage products, categories, brands, advertisements, and discounts.',
    descAr: 'يمكنه إدارة المنتجات والتصنيفات والعلامات التجارية والإعلانات والخصومات.',
    color: '#f59e0b',
  },
  {
    value: 4,
    en: 'Super Admin',
    ar: 'مدير عام',
    descEn: 'Full access to everything including user management and system settings.',
    descAr: 'صلاحية كاملة على كل شيء بما في ذلك إدارة المستخدمين وإعدادات النظام.',
    color: '#ef4444',
  },
];

const EditUserModal = ({ user, isOpen, onClose, onSuccess }) => {
  const { isRtl } = useLocalization();
  const [form, setForm] = useState({
    fullName: user.fullName || '',
    email: user.email || '',
    role: user.role || 1,
    isActive: user.isActive !== false,
    newPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const selectedRole = ROLES.find(r => r.value === Number(form.role)) || ROLES[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.fullName.trim() || !form.email.trim()) {
      setError(isRtl ? 'الاسم والبريد الإلكتروني مطلوبان' : 'Full name and email are required');
      return;
    }
    setIsLoading(true);
    try {
      await userService.update(user.id, {
        fullName: form.fullName,
        email: form.email,
        role: Number(form.role),
        isActive: form.isActive,
        newPassword: form.newPassword || null,
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || (isRtl ? 'حدث خطأ' : 'An error occurred'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isRtl ? `تعديل: ${user.username}` : `Edit: ${user.username}`}
      size="large"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{isRtl ? 'إلغاء' : 'Cancel'}</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? (isRtl ? 'جاري الحفظ...' : 'Saving...') : (isRtl ? 'حفظ التغييرات' : 'Save Changes')}
          </Button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-5)' }}>
        {/* Left — form */}
        <div>
          {error && (
            <div className="alert alert-error" style={{ marginBottom: 'var(--sp-4)' }}>{error}</div>
          )}

          <Input
            label={isRtl ? 'الاسم الكامل *' : 'Full Name *'}
            value={form.fullName}
            onChange={e => setForm({ ...form, fullName: e.target.value })}
          />
          <Input
            label={isRtl ? 'البريد الإلكتروني *' : 'Email *'}
            type="email"
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
          />
          <Input
            label={isRtl ? 'كلمة مرور جديدة (اتركها فارغة للإبقاء على الحالية)' : 'New Password (leave blank to keep current)'}
            type="password"
            value={form.newPassword}
            onChange={e => setForm({ ...form, newPassword: e.target.value })}
          />

          <div className="form-group">
            <label className="form-label">{isRtl ? 'الدور' : 'Role'}</label>
            <select
              className="form-select"
              value={form.role}
              onChange={e => setForm({ ...form, role: Number(e.target.value) })}
            >
              {ROLES.map(r => (
                <option key={r.value} value={r.value}>{isRtl ? r.ar : r.en}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={e => setForm({ ...form, isActive: e.target.checked })}
              />
              {isRtl ? 'الحساب نشط' : 'Account Active'}
            </label>
          </div>
        </div>

        {/* Right — role info */}
        <div>
          <p className="form-label" style={{ marginBottom: 'var(--sp-3)' }}>
            {isRtl ? 'صلاحيات الأدوار' : 'Role Permissions'}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
            {ROLES.map(r => {
              const active = Number(form.role) === r.value;
              return (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setForm({ ...form, role: r.value })}
                  style={{
                    textAlign: isRtl ? 'right' : 'left',
                    padding: 'var(--sp-3) var(--sp-4)',
                    borderRadius: 'var(--r-lg)',
                    border: `2px solid ${active ? r.color : 'var(--border)'}`,
                    background: active ? r.color + '12' : 'var(--surface)',
                    cursor: 'pointer',
                    transition: 'all 0.18s ease',
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm)', color: active ? r.color : 'var(--text)', marginBottom: 4 }}>
                    {isRtl ? r.ar : r.en}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                    {isRtl ? r.descAr : r.descEn}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EditUserModal;
